/**
 * Sanitize untrusted HTML before injecting via `dangerouslySetInnerHTML`.
 * Used by CMS pages and Campus lesson bodies (admin-edited Markdown→HTML).
 *
 * Allows the rich-text constructs we actually use in the editor (lists, headings,
 * tables, links, images, code blocks). Blocks <script>, <iframe>, event handlers,
 * and javascript: URLs by default — but explicitly whitelists YouTube/Vimeo
 * iframes (these are added by the Campus video embed feature and the trusted
 * RT editor).
 */
import DOMPurify from 'dompurify';
import { marked } from 'marked';

const ALLOWED_IFRAME_HOSTS = [
    'www.youtube.com',
    'www.youtube-nocookie.com',
    'youtube.com',
    'player.vimeo.com',
];

if (typeof window !== 'undefined' && DOMPurify && DOMPurify.addHook) {
    DOMPurify.addHook('uponSanitizeElement', (node, data) => {
        if (data.tagName === 'iframe') {
            try {
                const src = node.getAttribute && node.getAttribute('src');
                if (!src) return;
                const url = new URL(src, window.location.origin);
                if (!ALLOWED_IFRAME_HOSTS.includes(url.host)) {
                    node.parentNode && node.parentNode.removeChild(node);
                }
            } catch (_e) {
                node.parentNode && node.parentNode.removeChild(node);
            }
        }
    });
}

/**
 * Returns sanitized HTML safe to render with `dangerouslySetInnerHTML`.
 * Empty / non-string inputs return an empty string.
 */
export function sanitizeHtml(input) {
    if (!input || typeof input !== 'string') return '';
    return DOMPurify.sanitize(input, {
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target', 'referrerpolicy'],
        FORBID_TAGS: ['style', 'script'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus'],
    });
}

const HTML_BLOCK_RE = /<\/?(p|div|h[1-6]|ul|ol|li|table|blockquote|br|strong|em|b|i|img|a|iframe|pre|code)\b/i;

/** Render lesson/CMS content: converts Markdown to HTML when needed, then sanitizes. */
export function renderRichContent(input) {
    if (!input || typeof input !== 'string') return '';
    let html = input;
    if (!HTML_BLOCK_RE.test(input)) {
        try { html = marked.parse(input, { breaks: true }); } catch { html = input; }
    }
    return sanitizeHtml(html);
}

export default sanitizeHtml;
